import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  invoiceDetailsListColumns,
  ViewQuotationListColumns,
} from "../../../helpers/adminConstants/TableConstants";
import { ViewSalesInvoicesWrapper } from "./styled";
import {
  BackButton,
  Box,
  ButtonStyledIconLeft,
  HeadingStyled,
  SpanStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import DynamicTableList from "../../../components/common/tables/DynamicTableList";
import { toast } from "react-toastify";
import SalesInvoiceApi from "../../../services/api/salesInvoiceApi";
import { useUser } from "../../../context/userContext";
import { EditIcon, LogoAr, LogoEn } from "../../../components/styled/AllImages";
import ListTable from "../../../components/common/tables/ListTable";
import { generateInvoicePDF } from "../../../utils/generationPdf";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import { toBase64 } from "../../../utils";

const ViewSalesInvoice = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { user } = useUser();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const childInvoice = searchParams.get("child");
  const [loading, setLoading] = useState(false);
  const [rowsData, setRowsData] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);

  useEffect(() => {
    if (user?.organization?.logo) {
      toBase64(
        `${process.env.REACT_APP_ASSET_URL}/${user?.organization?.logo}`
      ).then(setLogoBase64);
    } else {
      setLogoBase64(language === "en" ? LogoEn : LogoAr);
    }
  }, []);

  const onDelete = (row) => {
    setInvoiceId(row?.id);
    setShowDeleteModal(true);
  };

  const handleView = (invoice) => {
    navigate(`/sales-invoices/${invoice?.id}/view-invoice?child=${true}`);
  };

  const handleChildInvoiceDownload = async (row) => {
    const data = await fetchInvoiceDetails(row?.id);
    await generateInvoicePDF(data, logoBase64, "sales_invoice.pdf");
  };

  const fetchInvoiceDetails = async (id) => {
    try {
      const { data, code } = await SalesInvoiceApi.viewInvoice(id);
      if (code === 200 && data) {
        return data;
      }
    } catch (error) {
      toast.error(error?.message);
      return null;
    } finally {
    }
  };

  const handleDelete = async () => {
    try {
      setLoadingDelete(true);
      const response = await SalesInvoiceApi.deleteInvoice(invoiceId);
      if (response.code === 200) {
        toast.success(t("invoice_deleted_successfully"));
        setShowDeleteModal(false);
        ViewSalesInvoice();
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {
    } finally {
      setLoadingDelete(false);
    }
  };

  const ViewSalesInvoice = async () => {
    try {
      setLoading(true);
      const { data, code } = await SalesInvoiceApi.viewInvoice(id);
      if (code === 200 && data) {
        const { products, client_name_en, client_name_ar } = data;

        setRowsData(
          products.map((item, index) => ({
            no: index + 1,
            category:
              language === "en"
                ? item?.category_name_en
                : item?.category_name_ar,
            product:
              language === "en" ? item.product_name_en : item.product_name_ar,
            quantity: Number(item.quantity),
            unit_price: `${Number(item.unit_price)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
            unit: item.unit,
            discount: `${Number(item.discount)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
            total: `${Number(item.total)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
            net_total: `${Number(item.net_total)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
            vat: `${Number(item.vat)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
            subtotal: `${Number(item.subtotal)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
          }))
        );
        setInvoiceDetails({
          ...data,
          client_name: language === "en" ? client_name_en : client_name_ar,
        });
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    ViewSalesInvoice();
  }, [id]);

  return (
    <ViewSalesInvoicesWrapper>
      <>
        <Box className="content-head">
          <Box className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-3">
            <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
              <BackButton
                label={t("invoice_details")}
                onClick={() => navigate(
                    childInvoice && invoiceDetails?.parent_id
                      ? `/sales-invoices/${invoiceDetails?.parent_id}/view-invoice`
                      : `/sales-invoices`
                  )}
              />
              <Breadcrumbs />
            </Box>
            {!childInvoice && (
              <Box>
                <ButtonStyledIconLeft
                  label={t("edit_invoice")}
                  icon={EditIcon}
                  color="var(--white)"
                  onClick={() => navigate(`/sales-invoices/${id}/edit-invoice`)}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box className="table-wrapper">
          {loading ? (
            <ThemeLoader />
          ) : (
            <>
              <Box>
                <SpanStyled color="var(--text2)">{t("invoice_to")}</SpanStyled>
                <SubHeadingStyled color="var(--text)" size="20px" weight="600">
                  {invoiceDetails?.client_name}
                </SubHeadingStyled>
                <Box className="d-flex flex-column mt-3 info-wrapper">
                  <Box className="d-flex gap-3 align-items-center justify-content-between sub-title-wrapper">
                    <SpanStyled color="var(--text2)">
                      {t("invoice_id")}
                    </SpanStyled>
                    <SubHeadingStyled
                      color="var(--text)"
                      size="14px"
                      weight="500"
                    >
                      {invoiceDetails?.invoice_no}
                    </SubHeadingStyled>
                  </Box>
                  <hr
                    style={{
                      color: "var(--text2)",
                      backgroundColor: "var(--text2)",
                    }}
                  />
                  <Box className="d-flex align-items-center justify-content-between sub-title-wrapper mb-3">
                    <SpanStyled color="var(--text2)">
                      {t("issue_date")}
                    </SpanStyled>
                    <SubHeadingStyled
                      color="var(--text)"
                      size="14px"
                      weight="500"
                    >
                      {invoiceDetails?.date}
                    </SubHeadingStyled>
                  </Box>
                  <Box className="d-flex align-items-center justify-content-between sub-title-wrapper">
                    <SpanStyled color="var(--text2)">
                      {t("invoice_due")}
                    </SpanStyled>
                    <SubHeadingStyled
                      color="var(--text)"
                      size="14px"
                      weight="500"
                    >
                      {invoiceDetails?.invoice_due}
                    </SubHeadingStyled>
                  </Box>
                </Box>
              </Box>
              <Box>
                <DynamicTableList
                  fields={rowsData}
                  cols={ViewQuotationListColumns}
                  readOnly={true}
                />
              </Box>
              <Box>
                <Box className="footer-wrapper">
                  {[
                    { label: "subtotal", value: invoiceDetails?.sub_total },
                    { label: "discount", value: invoiceDetails?.discount },
                    {
                      label: "total_after_discount",
                      value: invoiceDetails?.amount_after_discount,
                    },
                    {
                      label: "taxable_amount",
                      value: invoiceDetails?.tax_value,
                    },
                    {
                      label: "total_amount",
                      value: invoiceDetails?.total_amount,
                    },
                  ]?.map((item, idx) => (
                    <Box key={idx}>
                      <SpanStyled color="var(--text2)" size="14px">
                        {t(item?.label)}
                      </SpanStyled>
                      <SubHeadingStyled
                        color="var(--text)"
                        size="14px"
                        weight="500"
                      >
                        {item?.value} &nbsp;
                        {user?.organization?.currency?.stringCode || t("sar")}
                      </SubHeadingStyled>
                    </Box>
                  ))}
                </Box>
                {invoiceDetails?.notes && (
                  <Box className="">
                    <SpanStyled color="var(--text2)" size="14px">
                      {t("note")}
                    </SpanStyled>
                    <SubHeadingStyled
                      color="var(--text)"
                      size="14px"
                      weight="500"
                    >
                      {invoiceDetails?.notes}
                    </SubHeadingStyled>
                  </Box>
                )}
              </Box>
              {!childInvoice && (
                <Box
                  style={{ backgroundColor: "var(--bgPrimary)" }}
                  className="rounded-4 p-3"
                >
                  <Box>
                    <HeadingStyled color="var(--text)">
                      {t("invoice_versions")}
                    </HeadingStyled>
                  </Box>
                  <ListTable
                    options={["view", "download", "delete"]}
                    rows={invoiceDetails?.child_invoices}
                    columns={invoiceDetailsListColumns}
                    onDelete={onDelete}
                    onOpen={handleView}
                    onDownload={handleChildInvoiceDownload}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </>

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        title={t("invoice")}
        isLoading={loadingDelete}
      />
    </ViewSalesInvoicesWrapper>
  );
};

export default ViewSalesInvoice;
